import {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import { createPortal } from 'react-dom';
import {
  Key,
  Plus,
  Upload,
  Edit,
  Trash2,
  Copy,
  CheckCircle,
  XCircle,
  RefreshCw,
  Settings,
  AlertCircle,
  Activity,
  Zap,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../utils/dateFormat';
import { formatCompactNumber } from '../../utils/formatNumber';
import PageTitle from '../../components/PageTitle';

type TrendRange = '12h' | '1d' | '7d' | '30d';

interface TrendPoint {
  label: string;
  value: number;
}

interface TrendData {
  requests: TrendPoint[];
  tokens: TrendPoint[];
}

interface ConfirmDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  onConfirm: () => void;
}

interface ApiKeyImportResult {
  summary: {
    total: number;
    imported: number;
    skipped: number;
    failed: number;
  };
  imported: string[];
  skipped: Array<{ key: string; reason: string }>;
  failed: Array<{ key: string; reason: string }>;
}

function DashboardApiKeys() {
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingKey, setEditingKey] = useState<any>(null);
  const { t } = useTranslation('dashboard');
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [trendRange, setTrendRange] = useState<TrendRange>('7d');
  const [trendData, setTrendData] = useState<TrendData | null>(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [sortField, setSortField] = useState<'tokens' | 'requests' | 'lastUsed'>('lastUsed');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPayload, setImportPayload] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<ApiKeyImportResult | null>(null);
  const importFileInputRef = useRef<HTMLInputElement>(null);

  const toggleSort = (field: 'tokens' | 'requests' | 'lastUsed') => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }: { field: 'tokens' | 'requests' | 'lastUsed' }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  };

  const sortedApiKeys = useMemo(() => {
    return [...apiKeys].sort((a, b) => {
      let av: number, bv: number;
      switch (sortField) {
        case 'tokens':
          av = a.tokenUsage?.totalTokens || 0;
          bv = b.tokenUsage?.totalTokens || 0;
          break;
        case 'requests':
          av = a.usage?.totalRequests || 0;
          bv = b.usage?.totalRequests || 0;
          break;
        case 'lastUsed':
          av = a.lastUsedAt ? new Date(a.lastUsedAt).getTime() : 0;
          bv = b.lastUsedAt ? new Date(b.lastUsedAt).getTime() : 0;
          break;
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
  }, [apiKeys, sortField, sortDir]);

  const loadTrends = useCallback(async (range: TrendRange) => {
    setTrendLoading(true);
    try {
      const response = await axios.get(`/api/auth/api-keys/trends?range=${range}`);
      setTrendData(response.data);
    } catch (error) {
      console.error('Failed to load trends:', error);
    } finally {
      setTrendLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApiKeys();
    loadUsers();
    loadTrends(trendRange);
  }, []);

  useEffect(() => {
    loadTrends(trendRange);
  }, [trendRange, loadTrends]);

  const loadApiKeys = async () => {
    try {
      const response = await axios.get('/api/auth/api-keys');
      setApiKeys(response.data);
    } catch (error: any) {
      console.error('Failed to load API keys:', error);
      toast.error(error.response?.data?.error || t('api_keys.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get('/api/admin/users');
      setUsers(response.data.users || []);
    } catch (error: any) {
      console.error('Failed to load users:', error);
      setUsers([]);
    }
  };

  const handleCreateKey = async (keyData: any) => {
    try {
      const response = await axios.post('/api/auth/api-keys', keyData);
      setApiKeys(prev => [response.data, ...prev]);
      setShowCreateModal(false);
      toast.success(t('api_keys.create_success'));
    } catch (error: any) {
      console.error('API key creation failed:', error.response?.data || error.message);
      toast.error(error.response?.data?.error || t('api_keys.create_failed'));
    }
  };

  const handleUpdateKey = async (id: string, keyData: any) => {
    try {
      const response = await axios.put(`/api/auth/api-keys/${id}`, keyData);
      setApiKeys(prev => prev.map(key => key.id === id ? response.data : key));
      setEditingKey(null);
      toast.success(t('api_keys.update_success'));
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('api_keys.update_failed'));
    }
  };

  const handleDeleteKey = (id: string) => {
    setConfirmDialog({
      isOpen: true,
      title: t('api_keys.delete_key'),
      message: t('api_keys.delete_confirm'),
      confirmText: t('common:delete'),
      onConfirm: async () => {
        try {
          await axios.delete(`/api/auth/api-keys/${id}`);
          setApiKeys(prev => prev.filter(key => key.id !== id));
          toast.success(t('api_keys.delete_success'));
        } catch (error: any) {
          toast.error(error.response?.data?.error || t('api_keys.delete_failed'));
        }
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t('common:copied_to_clipboard'));
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportLoading(false);
    setImportPayload('');
    setImportResult(null);
    if (importFileInputRef.current) {
      importFileInputRef.current.value = '';
    }
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const content = await file.text();
      setImportPayload(content);
    } catch {
      toast.error(t('api_keys.import_read_failed'));
    }
  };

  const handleImportKeys = async () => {
    let parsed: any;
    try {
      parsed = JSON.parse(importPayload);
    } catch {
      toast.error(t('api_keys.import_invalid_json'));
      return;
    }

    const apiKeysToImport = Array.isArray(parsed)
      ? parsed
      : (Array.isArray(parsed?.apiKeys) ? parsed.apiKeys : null);

    if (!apiKeysToImport || apiKeysToImport.length === 0) {
      toast.error(t('api_keys.import_empty'));
      return;
    }

    try {
      setImportLoading(true);
      const response = await axios.post('/api/auth/api-keys/import', { apiKeys: apiKeysToImport });
      setImportResult(response.data);
      await loadApiKeys();
      toast.success(t('api_keys.import_success'));
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('api_keys.import_error'));
    } finally {
      setImportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <PageTitle titleKey="api_keys.title" namespace="dashboard" descriptionKey="api_keys.subtitle" />
      <div>
        <h1 className="text-2xl font-semibold text-foreground tracking-tight">{t('api_keys.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('api_keys.subtitle')}</p>
      </div>

      {/* Header Actions */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          {t('api_keys.total_keys')} {apiKeys.length}
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowImportModal(true)}
            className="px-4 py-2 border border-border rounded-lg hover:bg-muted/50 transition-colors flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            {t('api_keys.import_keys')}
          </button>
          <button
            onClick={loadApiKeys}
            className="px-4 py-2 border border-border rounded-lg hover:bg-muted/50 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            {t('common:refresh')}
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {t('api_keys.create_key')}
          </button>
        </div>
      </div>

      {/* API Keys Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('api_keys.col_name')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('api_keys.col_key')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('api_keys.col_user')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('api_keys.col_status')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer select-none hover:text-foreground transition-colors"
                  onClick={() => toggleSort('requests')}
                >
                  <span className="inline-flex items-center gap-1">{t('api_keys.col_requests')} <SortIcon field="requests" /></span>
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer select-none hover:text-foreground transition-colors"
                  onClick={() => toggleSort('tokens')}
                >
                  <span className="inline-flex items-center gap-1 justify-end">{t('api_keys.col_tokens')} <SortIcon field="tokens" /></span>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('api_keys.col_created')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer select-none hover:text-foreground transition-colors"
                  onClick={() => toggleSort('lastUsed')}
                >
                  <span className="inline-flex items-center gap-1">{t('api_keys.col_last_used')} <SortIcon field="lastUsed" /></span>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('api_keys.col_actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {apiKeys.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-muted-foreground">
                    <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">{t('api_keys.no_keys')}</p>
                    <p className="text-sm">{t('api_keys.no_keys_desc')}</p>
                  </td>
                </tr>
              ) : (
                sortedApiKeys.map((apiKey) => (
                  <tr key={apiKey.id} className="hover:bg-muted/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-foreground">
                        {apiKey.name}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {apiKey.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                          {apiKey.key ? `hub_****${apiKey.key.slice(-4)}` : t('common:na')}
                        </code>
                        {apiKey.key && !apiKey.key.includes('...') && (
                          <button
                            onClick={() => copyToClipboard(apiKey.key)}
                            className="text-muted-foreground hover:text-foreground"
                            title={t('api_keys.copy_key')}
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-muted-foreground">
                        {apiKey.user?.email || t('common:unknown')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        apiKey.isActive
                          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
                          : 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400'
                      }`}>
                        {apiKey.isActive ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                        {apiKey.isActive ? t('common:active') : t('common:inactive')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {formatCompactNumber(apiKey.usage?.totalRequests || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground text-right">
                      {formatCompactNumber(apiKey.tokenUsage?.totalTokens || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {formatDateTime(apiKey.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {apiKey.lastUsedAt ? formatDateTime(apiKey.lastUsedAt) : t('common:never')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setEditingKey(apiKey)}
                          className="text-primary hover:text-primary/80"
                          title={t('api_keys.edit_key')}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteKey(apiKey.id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          title={t('api_keys.delete_key')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingKey) && (
        <ApiKeyModal
          apiKey={editingKey}
          users={users}
          onSave={editingKey ? handleUpdateKey : handleCreateKey}
          onClose={() => {
            setShowCreateModal(false);
            setEditingKey(null);
          }}
        />
      )}

      {showImportModal && (
        <ImportApiKeysModal
          payload={importPayload}
          importLoading={importLoading}
          result={importResult}
          fileInputRef={importFileInputRef}
          onPayloadChange={setImportPayload}
          onImport={handleImportKeys}
          onFileChange={handleImportFile}
          onClose={closeImportModal}
        />
      )}

      {/* Usage Statistics */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5" />
          {t('api_keys.usage_stats')}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center p-4 border border-border rounded-lg">
            <div className="text-2xl font-bold text-primary">{apiKeys.filter(k => k.isActive).length}</div>
            <div className="text-sm text-muted-foreground">{t('api_keys.active_keys')}</div>
          </div>
          <div className="text-center p-4 border border-border rounded-lg">
            <div className="text-2xl font-bold text-green-600">{apiKeys.length}</div>
            <div className="text-sm text-muted-foreground">{t('api_keys.total_keys_stat')}</div>
          </div>
          <div className="text-center p-4 border border-border rounded-lg">
            <div className="text-2xl font-bold text-purple-600">{users.length}</div>
            <div className="text-sm text-muted-foreground">{t('api_keys.total_users')}</div>
          </div>
          <div className="text-center p-4 border border-border rounded-lg">
            <div className="text-2xl font-bold text-orange-600">
              {formatCompactNumber(apiKeys.reduce((sum, key) => sum + (key.usage?.totalRequests || 0), 0))}
            </div>
            <div className="text-sm text-muted-foreground">{t('api_keys.total_requests')}</div>
          </div>
          <div className="text-center p-4 border border-border rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {formatCompactNumber(apiKeys.reduce((sum, key) => sum + (key.tokenUsage?.totalTokens || 0), 0))}
            </div>
            <div className="text-sm text-muted-foreground">{t('api_keys.total_tokens')}</div>
          </div>
        </div>
      </div>

      {/* Trend Charts */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
            <Activity className="h-5 w-5" />
            {t('api_keys.request_trends')}
          </h3>
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            {(['12h', '1d', '7d', '30d'] as TrendRange[]).map((r) => (
              <button
                key={r}
                onClick={() => setTrendRange(r)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  trendRange === r
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t(`api_keys.range_${r}`)}
              </button>
            ))}
          </div>
        </div>

        {trendLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TrendLineChart
              data={trendData?.requests || []}
              title={t('api_keys.request_trends')}
              color="#3b82f6"
              icon={<Activity className="h-4 w-4 text-blue-500" />}
            />
            <TrendLineChart
              data={trendData?.tokens || []}
              title={t('api_keys.token_trends')}
              color="#8b5cf6"
              icon={<Zap className="h-4 w-4 text-purple-500" />}
              formatValue={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}K` : String(v)}
            />
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {confirmDialog.isOpen && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
          onKeyDown={e => e.key === 'Escape' && setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
          tabIndex={-1}
        >
          <div className="bg-card border border-border rounded-xl shadow-lg w-full max-w-md overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground">
                    {confirmDialog.title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {confirmDialog.message}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex gap-3 justify-end">
                <button
                  onClick={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
                  className="px-4 py-2 text-sm font-medium text-foreground bg-muted hover:bg-muted/80 rounded-lg transition-colors"
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={confirmDialog.onConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  {confirmDialog.confirmText || t('common:delete')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// API Key Modal Component
function ApiKeyModal({ apiKey, users, onSave, onClose }: {
  apiKey?: any;
  users: any[];
  onSave: (id: string, data: any) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('dashboard');
  const [formData, setFormData] = useState({
    name: apiKey?.name || '',
    description: apiKey?.description || '',
    userId: apiKey?.user?.id || '',
    isActive: apiKey?.isActive ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields for new API keys
    if (!apiKey) {
      if (!formData.userId.trim()) {
        toast.error(t('api_keys.select_user_error'));
        return;
      }
      if (!formData.name.trim()) {
        toast.error(t('api_keys.name_required'));
        return;
      }
    }

    if (apiKey) {
      const updateData: any = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        allowedModels: [],
        isActive: formData.isActive
      };
      onSave(apiKey.id, updateData);
    } else {
      const createData = {
        userId: formData.userId,
        name: formData.name.trim(),
        description: formData.description.trim(),
        allowedModels: []
      };
      onSave('', createData);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onKeyDown={e => e.key === 'Escape' && onClose()}
      tabIndex={-1}
    >
      <div className="bg-card border border-border rounded-xl shadow-lg max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              {apiKey ? t('api_keys.edit_title') : t('api_keys.create_title')}
            </h3>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('api_keys.col_name')}
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder={t('api_keys.name_placeholder')}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('api_keys.description')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder={t('api_keys.desc_placeholder')}
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('api_keys.user_label')}
              </label>
              <select
                value={formData.userId}
                onChange={(e) => setFormData(prev => ({ ...prev, userId: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                required
              >
                <option value="">{t('api_keys.select_user')}</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.email}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                className="h-4 w-4 text-primary focus:ring-primary border-border rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-foreground">
                {t('api_keys.active_checkbox')}
              </label>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-border text-foreground rounded-lg hover:bg-muted/50 transition-colors"
              >
                {t('common:cancel')}
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                {apiKey ? t('api_keys.update') : t('api_keys.create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function TrendLineChart({ data, title, color, icon, formatValue }: {
  data: TrendPoint[];
  title: string;
  color: string;
  icon: React.ReactNode;
  formatValue?: (v: number) => string;
}) {
  const { t } = useTranslation('dashboard');
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const fmt = formatValue || ((v: number) => formatCompactNumber(v));

  if (!data.length) {
    return (
      <div className="border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          {icon}
          <h4 className="text-sm font-semibold text-foreground">{title}</h4>
        </div>
        <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
          {t('api_keys.no_data')}
        </div>
      </div>
    );
  }

  const values = data.map(d => d.value);
  const maxValue = Math.max(...values);
  const minValue = Math.min(...values);
  const range = maxValue - minValue || 1;
  const total = values.reduce((a, b) => a + b, 0);
  const chartId = title.replace(/\s+/g, '-').toLowerCase();

  const showEveryNth = data.length > 12 ? Math.ceil(data.length / 8) : 1;

  const fmtAxis = (v: number) => {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
    return v.toFixed(0);
  };

  const ticks = [1, 0.75, 0.5, 0.25, 0].map(pct => minValue + range * pct);

  return (
    <div className="border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {icon}
          <h4 className="text-sm font-semibold text-foreground">{title}</h4>
        </div>
        <span className="text-xs font-medium text-muted-foreground">{t('api_keys.chart_total', { value: fmt(total) })}</span>
      </div>
      <div className="relative">
        <div className="flex">
          <div className="flex flex-col justify-between shrink-0 w-10 text-right pr-2" style={{ height: 180, paddingTop: 16, paddingBottom: 16 }}>
            {ticks.map((v, i) => (
              <span key={i} className="text-[10px] leading-none text-muted-foreground">{fmtAxis(v)}</span>
            ))}
          </div>
          <div className="flex-1 min-w-0 relative" onMouseLeave={() => setHoverIndex(null)}>
            <svg width="100%" height="180" viewBox="0 0 400 180" preserveAspectRatio="none" className="overflow-visible">
              <defs>
                <linearGradient id={`grad-${chartId}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity="0.2" />
                  <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
              </defs>

              {[0, 0.25, 0.5, 0.75, 1].map((pct) => (
                <line key={pct} x1="20" y1={20 + (1 - pct) * 120} x2="380" y2={20 + (1 - pct) * 120} stroke="currentColor" strokeWidth="0.5" className="text-border" />
              ))}

              <polygon
                fill={`url(#grad-${chartId})`}
                points={
                  `20,140 ` +
                  data.map((point, index) => {
                    const x = data.length === 1 ? 200 : (index / (data.length - 1)) * 360 + 20;
                    const y = 140 - ((point.value - minValue) / range) * 120;
                    return `${x},${y}`;
                  }).join(' ') +
                  ` 380,140`
                }
              />

              <polyline
                fill="none"
                stroke={color}
                strokeWidth="2.5"
                strokeLinejoin="round"
                strokeLinecap="round"
                points={
                  data.map((point, index) => {
                    const x = data.length === 1 ? 200 : (index / (data.length - 1)) * 360 + 20;
                    const y = 140 - ((point.value - minValue) / range) * 120;
                    return `${x},${y}`;
                  }).join(' ')
                }
              />

              {data.map((point, index) => {
                const x = data.length === 1 ? 200 : (index / (data.length - 1)) * 360 + 20;
                const y = 140 - ((point.value - minValue) / range) * 120;
                const isHovered = hoverIndex === index;
                return (
                  <circle key={`${point.label}-${index}`} cx={x} cy={y} r={isHovered ? 5 : 3} fill={isHovered ? 'white' : color} stroke={isHovered ? color : 'none'} strokeWidth={isHovered ? 2 : 0} />
                );
              })}

              {hoverIndex !== null && (() => {
                const x = data.length === 1 ? 200 : (hoverIndex / (data.length - 1)) * 360 + 20;
                return <line x1={x} y1="20" x2={x} y2="140" stroke={color} strokeWidth="1" strokeDasharray="4 2" opacity="0.5" />;
              })()}
            </svg>

            <div className="absolute inset-0 flex" style={{ pointerEvents: 'auto' }}>
              {data.map((point, index) => {
                const pctY = ((point.value - minValue) / range);
                return (
                  <div
                    key={`hover-${index}`}
                    className="flex-1 h-full relative"
                    onMouseEnter={() => setHoverIndex(index)}
                  >
                    {hoverIndex === index && (
                      <div
                        className="absolute z-10 px-2.5 py-1.5 rounded-lg shadow-lg border border-border bg-popover text-popover-foreground text-xs whitespace-nowrap pointer-events-none"
                        style={{
                          bottom: `${pctY * 66.7 + 22}%`,
                          left: '50%',
                          transform: 'translateX(-50%)',
                        }}
                      >
                        <div className="font-medium">{point.label}</div>
                        <div style={{ color }}>{fmt(point.value)}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex justify-between mt-2 pl-10 pr-2">
          {data.map((point, index) => (
            index % showEveryNth === 0 || index === data.length - 1 ? (
              <div key={`${point.label}-${index}`} className="text-center" style={{ minWidth: 0, flex: '0 0 auto' }}>
                <div className="text-[10px] text-muted-foreground truncate">{point.label}</div>
              </div>
            ) : null
          ))}
        </div>
      </div>
    </div>
  );
}

export default DashboardApiKeys;

function ImportApiKeysModal({
  payload,
  importLoading,
  result,
  fileInputRef,
  onPayloadChange,
  onImport,
  onFileChange,
  onClose,
}: {
  payload: string;
  importLoading: boolean;
  result: ApiKeyImportResult | null;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onPayloadChange: (value: string) => void;
  onImport: () => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('dashboard');
  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
      onKeyDown={e => e.key === 'Escape' && onClose()}
      tabIndex={-1}
    >
      <div className="bg-card border border-border rounded-xl shadow-lg max-w-3xl w-full">
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-foreground">{t('api_keys.import_keys')}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t('api_keys.import_subtitle')}</p>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          {result ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 border border-border rounded-lg text-center"><div className="text-2xl font-semibold text-foreground">{result.summary.total}</div><div className="text-sm text-muted-foreground">{t('api_keys.import_total')}</div></div>
                <div className="p-4 border border-border rounded-lg text-center"><div className="text-2xl font-semibold text-emerald-600">{result.summary.imported}</div><div className="text-sm text-muted-foreground">{t('api_keys.import_imported')}</div></div>
                <div className="p-4 border border-border rounded-lg text-center"><div className="text-2xl font-semibold text-amber-600">{result.summary.skipped}</div><div className="text-sm text-muted-foreground">{t('api_keys.import_skipped')}</div></div>
                <div className="p-4 border border-border rounded-lg text-center"><div className="text-2xl font-semibold text-red-600">{result.summary.failed}</div><div className="text-sm text-muted-foreground">{t('api_keys.import_failed')}</div></div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">{t('api_keys.import_imported')}</label>
                  <textarea readOnly value={result.imported.join('\n')} rows={6} className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">{t('api_keys.import_skipped')}</label>
                  <textarea readOnly value={result.skipped.map((item) => `${item.key}: ${item.reason}`).join('\n')} rows={6} className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">{t('api_keys.import_failed')}</label>
                  <textarea readOnly value={result.failed.map((item) => `${item.key}: ${item.reason}`).join('\n')} rows={6} className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground" />
                </div>
              </div>
              <div className="flex justify-end">
                <button onClick={onClose} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">{t('api_keys.import_close')}</button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <input ref={fileInputRef} type="file" accept=".json,application/json" className="hidden" onChange={onFileChange} />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 border border-border rounded-lg hover:bg-muted/50 transition-colors flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  {t('api_keys.import_load_file')}
                </button>
              </div>
              <textarea
                value={payload}
                onChange={(event) => onPayloadChange(event.target.value)}
                rows={16}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground font-mono text-sm"
                placeholder='{"apiKeys":[{"key":"ether_xxx","name":"Imported Key","userEmail":"user@example.com"}]}'
              />
              <div className="flex gap-3 justify-end">
                <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg hover:bg-muted/50 transition-colors">{t('common:cancel')}</button>
                <button onClick={onImport} disabled={importLoading} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60">
                  {importLoading ? t('api_keys.import_importing') : t('api_keys.import_keys')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
